import EnergyUsageRecent from '@di-shared/features/history/energy-usage/EnergyUsageRecent';
import {ErrorBoundary} from 'react-error-boundary';
import React, {Suspense, useState} from 'react';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useAvailableComponentEu from '@di-shared/features/home/eu/useAvailableComponent.eu';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ConsentBannerEu from '@di-shared/features/home/eu/ConsentBanner.eu';
import clsx from 'clsx';
import EnergyUsageDaily from '@di-shared/features/history/energy-usage/EnergyUsageDaily';
import {HistoryProps} from '@di-shared/features/history/index';
import {HistoryTab} from '@di-shared/pages/[vin]/history';
import DrivingInfoHeader from '@di-shared/features/history/driving-info/DrivingInfoHeader';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {isSameDate, isSameYearAndMonth, parseDateWithFormat, startDateOfMonth, subDate} from '@shared/dateUtils';
import DrivingInfoDistance from '@di-shared/features/history/driving-info/DrivingInfoDistance';
import {ERROR} from '@di-shared/const';
import {DRIVING_DISTANCE_GRAPH_EU} from '@di-shared/features/history/driving-info/DrivingInfoDistance/.eu';
import DrivingInfoTrip from '@di-shared/features/history/driving-info/DrivingInfoTrip';
import ReactDatePicker from 'react-datepicker';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';

export default function ({vin, tab, changeTab, month, changeMonth}: HistoryProps) {
  const selectedDate = parseDateWithFormat(month) ?? new Date();

  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          tab={tab}
          changeTab={changeTab}
          month={month}
          changeMonth={changeMonth}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, tab, changeTab, month, changeMonth}: HistoryProps) {
  const {t} = useInferableTranslation();
  const {checkAvailableComponent} = useAvailableComponentEu({vin});
  const [selectedDate, setSelectedDate] = useState(parseDateWithFormat(month) ?? new Date());

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_absolute"
        left={<HeaderBackButton />}
      >
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_LN_my_trips')}</h1>
      </Header>
      <main className="content page_my_trips">
        <ConsentBannerEu />
        {checkAvailableComponent('EV_ENERGY_USAGE') && (
          <div
            className="tabbar_txt tabbar_sticky"
            role="tablist"
          >
            <div className="inner">
              <button
                type="button"
                className={clsx('tabbar_item body_large_r', {'is-selected': tab === HistoryTab.DRIVING_INFO})}
                onClick={() => changeTab(HistoryTab.DRIVING_INFO)}
                id={`${HistoryTab.DRIVING_INFO}_tab`}
                role="tab"
                aria-selected={tab === HistoryTab.DRIVING_INFO}
                aria-controls={`${HistoryTab.DRIVING_INFO}_tabpanel`}
                tabIndex={tab === HistoryTab.DRIVING_INFO ? 0 : -1}
              >
                <span className="txt">{t('ctn_mytrips_tab_01')}</span>
              </button>
              <button
                type="button"
                className={clsx('tabbar_item body_large_r', {'is-selected': tab === HistoryTab.EV_ENERGY_USAGE})}
                onClick={() => changeTab(HistoryTab.EV_ENERGY_USAGE)}
                id={`${HistoryTab.EV_ENERGY_USAGE}_tab`}
                role="tab"
                aria-selected={tab === HistoryTab.EV_ENERGY_USAGE}
                aria-controls={`${HistoryTab.EV_ENERGY_USAGE}_tabpanel`}
                tabIndex={tab === HistoryTab.EV_ENERGY_USAGE ? 0 : -1}
              >
                <span className="txt">{t('ctn_mytrips_tab_02')}</span>
              </button>
            </div>
          </div>
        )}
        {tab === HistoryTab.DRIVING_INFO ? (
          <div
            className="tab_content monthly_driving"
            id={`${HistoryTab.DRIVING_INFO}_tabpanel`}
            aria-labelledby={`${HistoryTab.DRIVING_INFO}_tab`}
          >
            <section className="section">
              <DrivingInfoHeader
                vin={vin}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                changeMonth={changeMonth}
              />
              <DrivingInfoDistance
                vin={vin}
                selectedDate={selectedDate}
              />
              <DrivingInfoTrip
                vin={vin}
                selectedDate={selectedDate}
              />
            </section>
          </div>
        ) : (
          <div
            className="tab_content energy_usage"
            id={`${HistoryTab.EV_ENERGY_USAGE}_tabpanel`}
            aria-labelledby={`${HistoryTab.EV_ENERGY_USAGE}_tab`}
          >
            <EnergyUsageRecent vin={vin} />
            <div className="divider divider_thick" />
            <EnergyUsageDaily vin={vin} />
          </div>
        )}
      </main>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol, defaultEnergyEfficiencySymbol, defaultSpeedSymbol} = useUnitConverter();
  const today = new Date();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_EU.LENGTH).keys()).reverse();

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_with_tab"
        left={<HeaderBackButton />}
      >
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_LN_my_trips')}</h1>
      </Header>
      <main className="content page_my_trips">
        <div className="tab_content monthly_driving">
          <section className="section">
            <div className="section_heading type_main">
              <button
                type="button"
                className="btn_monthly body_medium_b skeleton"
                style={{width: '160px', height: '20px'}}
              />
              <h3
                className="section_heading_title skeleton"
                style={{width: '200px', height: '28px'}}
              />
              <span
                className="section_heading_caption skeleton"
                style={{width: '160px', height: '20px'}}
              />
            </div>
            <div className="section_visual skeleton">
              <ImageSelector
                alt="section_visual_img"
                className="section_visual_img"
                kiaLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
              />
            </div>
            <div className="section_container skeleton">
              <div className="section_container_header">
                <h4 className="title_xsmall_b">
                  {t('msg_mytrips_distance_01', {
                    month: CldrDatetimeFormatter.format(today, 'MMM'),
                    n: ERROR.DASH + defaultDistanceSymbol,
                  })}
                </h4>
              </div>
              <ul className="statistics_summary_list type_center">
                <li className="statistics_summary_item">
                  <span className="statistics_summary_key label_large_r">{t('ctn_mytirps_this_month')}</span>
                  <span className="statistics_summary_val">
                    <span className="title_small_b">{ERROR.DASH}</span>
                    <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
                  </span>
                </li>
                <li className="statistics_summary_item">
                  <span className="statistics_summary_key label_large_r">{t('ctn_mytrips_cumulative_distance')}</span>
                  <span className="statistics_summary_val">
                    <span className="title_small_b">{ERROR.DASH}</span>
                    <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
                  </span>
                </li>
              </ul>
              <div className="statistics_graph_area distance_statistics">
                <div className="bar_graph">
                  {errorDrivingDistances.map((month) => {
                    const legendMonth = subDate(today, {months: month});
                    const isSelectedMonth = isSameYearAndMonth(legendMonth, today);
                    return (
                      <div
                        key={month}
                        className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                      >
                        {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                        <span
                          className="graph_bar"
                          style={{height: `${DRIVING_DISTANCE_GRAPH_EU.MIN_HEIGHT}px`}}
                        />
                        <span className="graph_title">{CldrDatetimeFormatter.format(legendMonth, 'MMM')}</span>
                      </div>
                    );
                  })}
                  <div
                    className="bar_graph_average"
                    style={{top: `${DRIVING_DISTANCE_GRAPH_EU.AREA_HALF}px`}}
                  >
                    <span className="average_line" />
                    <span className="average_text_box">
                      <span className="average_text">{t('ctn_mytrips_average')}</span>
                      <span className="average_value">{ERROR.DASH}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="section_container skeleton">
              <div className="section_container_header">
                <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
                <span className="ic ic_more_vertical_20px" />
                <div
                  className="download_floating_button_area"
                  style={{display: 'none'}}
                >
                  <button className="download_btn_item">
                    <span className="ic ic_download_20px" />
                    <span className="download_btn_text">Download File</span>
                  </button>
                  <button className="download_btn_item">
                    <span className="ic ic_delete_20px" />
                    <span className="download_btn_text">Delete Trip</span>
                  </button>
                </div>
              </div>
              <ReactDatePicker
                inline
                calendarClassName={'show-header'}
                locale={getDateFnsLocale()}
                disabledKeyboardNavigation
                onChange={() => {}}
                renderCustomHeader={() => null}
              />
              <ReactDatePicker
                inline
                calendarClassName={`show-first-week-1`}
                locale={getDateFnsLocale()}
                disabledKeyboardNavigation
                onChange={() => null}
                weekDayClassName={() => 'react-datepicker--disabled'}
                renderCustomHeader={() => null}
              />
              <div className="trip_summary">
                <div className="trip_date skeleton">
                  <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
                  <span className="trip_date_today">{t('ctn_mytrips_today')}</span>
                </div>
                <div className="trip_summary_list type_multi_line skeleton">
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('ctn_mytrips_distance')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
                    </div>
                  </div>
                  <div className="divider divider_vertical" />
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('ctn_mytrips_time')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
                    </div>
                  </div>
                  <div className="divider divider_vertical" />
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('ctn_mytrips_details_driving_safety_score')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{defaultEnergyEfficiencySymbol}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="trip_info skeleton">
                <div className="trip_info_head">
                  <span className="trip_info_item">{DashedDatetimeFormatter.formatRange(today, today, 'time')}</span>
                  <span className="trip_info_item">
                    {ERROR.DASH}
                    {defaultDistanceSymbol}
                  </span>
                </div>
              </div>
              <ul className="statistics_record_list skeleton">
                <li className="statistics_record_item">
                  <span className="statistics_record_key">{t('ctn_mytrips_details_idle_time')}</span>
                  <span className="statistics_record_val">
                    <span className="statistics_record_number">{ERROR.DASH}</span>
                    <span className="statistics_record_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                    <span className="statistics_record_number">{ERROR.DASH}</span>
                    <span className="statistics_record_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                  </span>
                </li>
                <div className="divider divider_vertical" />
                <li className="statistics_record_item">
                  <span className="statistics_record_key">{t('ctn_mytrips_details_avg_speed')}</span>
                  <span className="statistics_record_val">
                    <span className="statistics_record_number">{ERROR.DASH}</span>
                    <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
                  </span>
                </li>
                <div className="divider divider_vertical" />
                <li className="statistics_record_item">
                  <span className="statistics_record_key">{t('ctn_mytrips_details_top_speed')}</span>
                  <span className="statistics_record_val">
                    <span className="statistics_record_number">{ERROR.DASH}</span>
                    <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
                  </span>
                </li>
                <div className="divider divider_vertical" />
              </ul>
              <button
                className="btn btn_large btn_filled btn_full"
                type="button"
              >
                {t('T10060_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol, defaultEnergyEfficiencySymbol} = useUnitConverter();
  const today = new Date();
  const startDate = startDateOfMonth(selectedDate);
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const errorDrivingDistances = Array.from(Array(DRIVING_DISTANCE_GRAPH_EU.LENGTH).keys()).reverse();

  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_with_tab"
        left={<HeaderBackButton />}
        right={
          <button
            type="button"
            className="btn btn_ic"
            aria-label={t('T10005')}
          >
            <span
              className="ic ic_setting_24px"
              aria-hidden="true"
            />
          </button>
        }
      >
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_knowing_data_mytrips_title')}</h1>
      </Header>
      <main className="content page_my_trips">
        <div className="tab_content monthly_driving">
          <section className="section">
            <div className="section_heading type_main">
              <button
                type="button"
                className="btn_monthly body_medium_b"
              >
                {CldrDatetimeFormatter.format(startDate, 'yMMM')}
                <span
                  className="ic ic_arrow_down_16px"
                  aria-hidden="true"
                />
              </button>
              <h3 className="section_heading_title">{t('ctn_mytrips_my_driving_title')}</h3>
              <span className="section_heading_caption">
                {t('ctn_mytirps_updated')} {ERROR.DASH}
              </span>
            </div>
            <div className="section_visual">
              <ImageSelector
                alt="section_visual_img"
                className="section_visual_img"
                kiaLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/visual/my_trips_light.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/visual/my_trips.png')}
              />
            </div>
            <div className="section_container">
              <div className="section_container_header">
                <h4 className="title_xsmall_b">
                  {t('msg_mytrips_distance_01', {
                    month: CldrDatetimeFormatter.format(today, 'MMM'),
                    n: ERROR.DASH + defaultDistanceSymbol,
                  })}
                </h4>
              </div>
              <ul className="statistics_summary_list type_center">
                <li className="statistics_summary_item">
                  <span className="statistics_summary_key label_large_r">{t('ctn_mytirps_this_month')}</span>
                  <span className="statistics_summary_val">
                    <span className="title_small_b">{ERROR.DASH}</span>
                    <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
                  </span>
                </li>
                <li className="statistics_summary_item">
                  <span className="statistics_summary_key label_large_r">{t('ctn_mytrips_cumulative_distance')}</span>
                  <span className="statistics_summary_val">
                    <span className="title_small_b">{ERROR.DASH}</span>
                    <span className="statistics_summary_unit label_small_b">{defaultDistanceSymbol}</span>
                  </span>
                </li>
              </ul>
              <div className="statistics_graph_area distance_statistics">
                <div className="bar_graph">
                  {errorDrivingDistances.map((month) => {
                    const legendMonth = subDate(startDate, {months: month});
                    const isSelectedMonth = isSameYearAndMonth(legendMonth, startDate);
                    return (
                      <div
                        key={month}
                        className={clsx('bar_graph_item', isSelectedMonth && 'is-active')}
                      >
                        {isSelectedMonth && <span className="speech_bubble">{ERROR.DASH}</span>}
                        <span
                          className="graph_bar"
                          style={{height: `${DRIVING_DISTANCE_GRAPH_EU.MIN_HEIGHT}px`}}
                        />
                        <span className="graph_title">{CldrDatetimeFormatter.format(legendMonth, 'MMM')}</span>
                      </div>
                    );
                  })}
                  <div
                    className="bar_graph_average"
                    style={{top: `${DRIVING_DISTANCE_GRAPH_EU.AREA_HALF}px`}}
                  >
                    <span className="average_line" />
                    <span className="average_text_box">
                      <span className="average_text">{t('ctn_mytrips_average')}</span>
                      <span className="average_value">{ERROR.DASH}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="section_container">
              <div className="section_container_header">
                <h4 className="title_xxsmall">{t('ctn_mytrips_recent_driving_history')}</h4>
              </div>
              <ReactDatePicker
                inline
                calendarClassName={'show-header'}
                locale={getDateFnsLocale()}
                disabledKeyboardNavigation
                onChange={() => {}}
                renderCustomHeader={() => null}
              />
              <ReactDatePicker
                inline
                calendarClassName={`show-first-week-1`}
                locale={getDateFnsLocale()}
                disabledKeyboardNavigation
                onChange={() => null}
                weekDayClassName={() => 'react-datepicker--disabled'}
                renderCustomHeader={() => null}
              />
              <div className="trip_summary">
                <div className="trip_date">
                  <span className="trip_date_text">{CldrDatetimeFormatter.format(startDate, 'MMMMEEEEd')}</span>
                  {isSameDate(today, startDate) && <span className="trip_date_today">{t('ctn_mytrips_today')}</span>}
                </div>
                <div className="trip_summary_list type_multi_line">
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('ctn_mytrips_distance')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
                    </div>
                  </div>
                  <div className="divider divider_vertical" />
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('ctn_mytrips_time')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
                    </div>
                  </div>
                  <div className="divider divider_vertical" />
                  <div className="trip_summary_item">
                    <span className="trip_summary_title">{t('ctn_mytrips_details_driving_safety_score')}</span>
                    <div className="trip_summary_info">
                      <span className="trip_summary_value">{ERROR.DASH}</span>
                      <span className="trip_summary_unit">{defaultEnergyEfficiencySymbol}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="trip_info">
                <div className="empty_area">
                  <span className="ic ic_caution_32px" />
                  <span className="body_small_r">{t('msg_mytrips_recent_no_driving')}</span>
                </div>
              </div>
              <button
                className="btn btn_large btn_filled btn_full"
                type="button"
              >
                {t('msg_mytrips_button_text', {month: CldrDatetimeFormatter.format(startDate, 'MMM')})}
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
